import React, { useState } from 'react';
import {
  TextField,
  Button,
  Divider,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { authLogin } from '../../../store/authSlice';
import { setVisiblePopUp } from 'store/uiSlice';
import { useNavigate } from 'react-router-dom';
import google from 'assets/icons/google.svg';
import type { RootState, AppDispatch } from 'store/store';

export const SignIn: React.FC<{ onForgotPassword: () => void }> = ({
  onForgotPassword,
}): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loggedIn } = useSelector((state: RootState) => state.auth);

  const [email, setEmail] = useState<string>(process.env.REACT_APP_USER ?? '');
  const [password, setPassword] = useState<string>(
    process.env.REACT_APP_PASS ?? '',
  );
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setError(null);

    dispatch(
      authLogin({
        email,
        password,
        rememberMe: false,
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(setVisiblePopUp(false));
        navigate('/app');
      })
      .catch((err: { message?: string }) => {
        setError(err?.message ?? 'Login failed');
        // Keep console for now; swap for UI feedback if desired
        // eslint-disable-next-line no-console
        console.log(err?.message ?? 'Login failed');
      });
  };

  if (loggedIn) return <></>;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Login to Planoo
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <TextField
          label="Email"
          type="email"
          value={email}
          fullWidth
          margin="normal"
          required
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutline />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          fullWidth
          margin="normal"
          required
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPassword(e.target.value)
          }
          inputProps={{ minLength: 6 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((show) => !show)}
                  edge="end"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 1 }}
        >
          <Button
            variant="text"
            size="small"
            onClick={onForgotPassword}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 400,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#009944',
              },
            }}
          >
            Forgot Password?
          </Button>
        </Box>

        <Button
          type="submit"
          size="large"
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            backgroundColor: '#009944',
            color: '#fff',
            fontWeight: 500,
            borderRadius: '4px',
            fontSize: '16px',
            '&:hover': {
              backgroundColor: '#007c36',
            },
          }}
        >
          Login
        </Button>
      </form>

      <form method="POST" action="/Account/ExternalLogin">
        <input type="hidden" name="provider" defaultValue="Google" />
        <input type="hidden" name="returnUrl" defaultValue="/app" />

        <Button
          type="submit"
          fullWidth
          size="large"
          variant="outlined"
          sx={{ mt: 2 }}
        >
          <img
            src={google}
            alt="Google icon"
            style={{ marginRight: '8px', marginBottom: '2px' }}
          />
          Sign In with Google
        </Button>
      </form>
    </Box>
  );
};
